import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, User, LogOut, Shield, X, Pencil, Sun, Moon } from "lucide-react";
import { useState, useEffect } from "react";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/noleggio/breve", label: "Breve termine", id: "breve-termine" },
  { to: "/noleggio/lungo-termine", label: "Lungo termine", id: "lungo-termine" },
  { to: "/noleggio/rent-to-buy", label: "Rent to Buy", id: "rent-to-buy" },
  { to: "/abbonamenti", label: "Abbonamento", id: "abbonamento" },
  { to: "/b2b", label: "B2B", id: "b2b" },
];

export default function Header({ onToggleTheme, theme = "light" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        theme === "dark"
          ? scrolled ? "bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10" : "bg-[#0a0a0a]/60 backdrop-blur-sm"
          : scrolled ? "bg-white/95 backdrop-blur-xl border-b border-black/10 shadow-sm" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <Logo size="sm" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `relative text-xs xl:text-sm uppercase tracking-widest font-semibold whitespace-nowrap hover:text-[#22ff66] transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:bg-[#22ff66] after:transition-[width] after:duration-300 hover:after:w-full ${
                  isActive ? "text-[#22ff66] after:w-full" : "text-white/80 after:w-0"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {onToggleTheme && (
            <button
              data-testid="theme-toggle"
              onClick={onToggleTheme}
              aria-label={theme === "dark" ? "Passa al tema chiaro" : "Passa al tema scuro"}
              title={theme === "dark" ? "Tema chiaro" : "Tema scuro"}
              className={`group relative w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                theme === "dark"
                  ? "text-white/70 hover:text-[#22ff66] hover:bg-white/5"
                  : "text-black/60 hover:text-[#06D6A0] hover:bg-black/5"
              }`}
            >
              {theme === "dark" ? (
                <Sun className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" strokeWidth={2} />
              ) : (
                <Moon className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-12" strokeWidth={2} />
              )}
            </button>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-white/30 transition">
                <User className="w-4 h-4" />
                <span className="hidden md:inline text-sm">{user.full_name?.split(" ")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0e0e0e] border-white/10 rounded-none min-w-[200px]">
                <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/account")} className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" /> Il mio account
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin/dashboard")} className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" /> Pannello Admin
                  </DropdownMenuItem>
                )}
                {user.is_admin && (
                  <DropdownMenuItem data-testid="menu-edit-home" onClick={() => nav("/?edit=1")} className="cursor-pointer">
                    <Pencil className="w-4 h-4 mr-2" /> Modalità Modifica Home
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }} className="cursor-pointer text-[#4EFF87]">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="hidden md:inline text-sm uppercase tracking-widest font-semibold hover:text-[#22ff66]">
                Accedi
              </Link>
              <Link
                to="/registrati"
                data-testid="header-register-link"
                className="hidden md:inline-block bg-[#22ff66] hover:bg-[#4EFF87] text-black px-5 py-2.5 text-sm uppercase tracking-widest font-semibold transition btn-red-glow rounded-full"
              >
                Registrati
              </Link>
            </>
          )}
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-2">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#050505] border-t border-white/5 px-6 py-6 space-y-4">
          {NAV.map((n) => (
            <Link
              key={n.id}
              to={n.to}
              data-testid={`mobile-nav-${n.id}`}
              onClick={() => setOpen(false)}
              className="block text-sm uppercase tracking-widest font-semibold py-1 hover:text-[#22ff66] transition-colors"
            >
              {n.label}
            </Link>
          ))}
          {!user && (
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 border border-white/20 px-4 py-2 text-center text-sm uppercase rounded-full">Accedi</Link>
              <Link to="/registrati" onClick={() => setOpen(false)} className="flex-1 bg-[#22ff66] text-black font-semibold px-4 py-2 text-center text-sm uppercase rounded-full">Registrati</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
